export const BLOCKER_CATEGORIES = {
  FEATURE_LOCKED: "FEATURE_LOCKED",
  LIMIT_REACHED: "LIMIT_REACHED",
  QUOTA_EXHAUSTED: "QUOTA_EXHAUSTED",
  INSUFFICIENT_CREDITS: "INSUFFICIENT_CREDITS",
};

const SUPPORTED_BLOCKER_CATEGORIES = new Set(Object.values(BLOCKER_CATEGORIES));

const LEGACY_ERROR_CODE_TO_BLOCKER = {
  FEATURE_NOT_AVAILABLE: BLOCKER_CATEGORIES.FEATURE_LOCKED,
  QUOTA_EXCEEDED: BLOCKER_CATEGORIES.QUOTA_EXHAUSTED,
  SUBSCRIPTION_INACTIVE: BLOCKER_CATEGORIES.FEATURE_LOCKED,
  PAST_DUE: BLOCKER_CATEGORIES.FEATURE_LOCKED,
  COMMERCIAL_BLOCKED: BLOCKER_CATEGORIES.FEATURE_LOCKED,
};

const FEATURE_LABELS = {
  canCreateWorkspace: "Workspace Creation",
  canInviteMembers: "Team Invitations",
  canUseWorkflows: "Workflows",
  canUseSequences: "Sequences",
  canUseOpportunities: "Opportunities",
  canUseTasking: "Task Management",
  canUseAdvancedReporting: "Advanced Reporting",
  canUseAdvancedGovernance: "Governance Controls",
  canUseSalesDashboard: "Sales Dashboard",
  canUsePlaybooks: "Playbooks",
  canUseBulkOperations: "Bulk Operations",
  canUseWebhooks: "Webhooks",
  canUseApiAccess: "API Access",
  canUseExportGovernance: "Export Governance",
  canUseSavedViews: "Saved Views",
};

const LIMIT_LABELS = {
  maxWorkspaces: "Workspace Limit",
  maxSeats: "Team Seat Limit",
  maxContacts: "Contact Limit",
  maxOpportunities: "Opportunity Limit",
  maxWorkflows: "Workflow Limit",
  maxSequences: "Sequence Limit",
  maxSavedViews: "Saved View Limit",
};

const QUOTA_LABELS = {
  monthlyTasks: "Monthly Tasks",
  monthlyCrmExports: "Monthly CRM Exports",
  monthlySearchQueries: "Monthly Search Queries",
  monthlyBulkOperations: "Monthly Bulk Operations",
};

const CREDIT_LABELS = {
  emailCredits: "Email Credits",
  discoveryExportCredits: "Discovery Export Credits",
  manualAdoptionCredits: "Manual Adoption Credits",
};

const LEGACY_CREDIT_LABELS = {
  emailUnlocksCount: "Email Credits",
  exportsCount: "Discovery Export Credits",
  adoptionsCount: "Manual Adoption Credits",
  searchQueriesCount: "Search Credits",
  bulkOperationsCount: "Bulk Credits",
};

const CATEGORY_META = {
  [BLOCKER_CATEGORIES.FEATURE_LOCKED]: {
    title: "Feature Locked",
    primaryCtaLabel: "Upgrade Plan",
    secondaryCtaLabel: "View Plan Details",
    primaryCtaTo: "/plan?section=features",
    secondaryCtaTo: "/plan?section=features",
  },
  [BLOCKER_CATEGORIES.LIMIT_REACHED]: {
    title: "Plan Limit Reached",
    primaryCtaLabel: "Upgrade Plan",
    secondaryCtaLabel: "View Plan Details",
    primaryCtaTo: "/plan?section=features",
    secondaryCtaTo: "/plan?section=features",
  },
  [BLOCKER_CATEGORIES.QUOTA_EXHAUSTED]: {
    title: "Quota Exhausted",
    primaryCtaLabel: "Manage Plan",
    secondaryCtaLabel: "View Billing",
    primaryCtaTo: "/plan?section=usage",
    secondaryCtaTo: "/plan?section=billing",
  },
  [BLOCKER_CATEGORIES.INSUFFICIENT_CREDITS]: {
    title: "Insufficient Credits",
    primaryCtaLabel: "Buy Credits",
    secondaryCtaLabel: "Manage Billing",
    primaryCtaTo: "/plan?section=credits",
    secondaryCtaTo: "/plan?section=billing",
  },
};

const toNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (value === Infinity) return "Unlimited";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed.toLocaleString();
};

const humanizeFallback = (key) => {
  if (!key) return "This action";
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
};

const resolveBlockerLabel = ({ category, details }) => {
  const featureKey = details?.featureKey;
  const limitKey = details?.limitKey;
  const quotaKey = details?.quotaKey;
  const creditKey = details?.creditKey;

  if (category === BLOCKER_CATEGORIES.FEATURE_LOCKED && featureKey) {
    return FEATURE_LABELS[featureKey] || humanizeFallback(featureKey);
  }
  if (category === BLOCKER_CATEGORIES.LIMIT_REACHED && limitKey) {
    return LIMIT_LABELS[limitKey] || humanizeFallback(limitKey);
  }
  if (category === BLOCKER_CATEGORIES.QUOTA_EXHAUSTED && quotaKey) {
    return QUOTA_LABELS[quotaKey] || humanizeFallback(quotaKey);
  }
  if (category === BLOCKER_CATEGORIES.INSUFFICIENT_CREDITS && creditKey) {
    return (
      CREDIT_LABELS[creditKey] ||
      LEGACY_CREDIT_LABELS[creditKey] ||
      humanizeFallback(creditKey)
    );
  }

  return "This action";
};

const resolveCategoryFromPayload = (payload = {}) => {
  const rawCategory = payload?.blockerCategory || payload?.category || null;
  if (SUPPORTED_BLOCKER_CATEGORIES.has(rawCategory)) return rawCategory;

  const errorCode = payload?.errorCode || payload?.error || null;
  if (!errorCode) return null;

  if (errorCode === "LIMIT_EXCEEDED") {
    const candidateKey =
      payload?.details?.creditKey ||
      payload?.details?.type ||
      payload?.details?.quotaKey ||
      payload?.details?.limitKey ||
      null;
    if (
      candidateKey === "emailCredits" ||
      candidateKey === "emailUnlocksCount" ||
      candidateKey === "discoveryExportCredits" ||
      candidateKey === "exportsCount"
    ) {
      return BLOCKER_CATEGORIES.INSUFFICIENT_CREDITS;
    }
    return BLOCKER_CATEGORIES.QUOTA_EXHAUSTED;
  }

  return LEGACY_ERROR_CODE_TO_BLOCKER[errorCode] || null;
};

const buildCategoryDescription = ({
  category,
  blockedItemLabel,
  message,
  details,
}) => {
  const planCode = details?.planCode || "your current";
  const currentUsage = toNumberOrNull(details?.currentUsage);
  const allowedLimit =
    details?.allowedLimit === Infinity
      ? Infinity
      : toNumberOrNull(details?.allowedLimit);
  const remaining =
    details?.remaining === Infinity
      ? Infinity
      : toNumberOrNull(details?.remaining);
  const requiredAmount = toNumberOrNull(details?.requiredAmount);
  const usageLine =
    currentUsage !== null && allowedLimit !== null
      ? `${formatNumber(currentUsage)} of ${formatNumber(allowedLimit)} used`
      : null;

  if (message) return message;

  if (category === BLOCKER_CATEGORIES.FEATURE_LOCKED) {
    return `${blockedItemLabel} is not included in the ${planCode} plan. Upgrade to continue.`;
  }
  if (category === BLOCKER_CATEGORIES.LIMIT_REACHED) {
    if (usageLine)
      return `${blockedItemLabel} has reached its plan limit (${usageLine}).`;
    return `${blockedItemLabel} has reached the maximum allowed by your current plan.`;
  }
  if (category === BLOCKER_CATEGORIES.QUOTA_EXHAUSTED) {
    if (usageLine)
      return `${blockedItemLabel} quota is exhausted for this billing cycle (${usageLine}).`;
    return `${blockedItemLabel} quota is exhausted for this billing cycle.`;
  }
  if (category === BLOCKER_CATEGORIES.INSUFFICIENT_CREDITS) {
    if (remaining !== null && requiredAmount !== null) {
      return `This action requires ${formatNumber(requiredAmount)} credits, but only ${formatNumber(remaining)} credits remain.`;
    }
    return `${blockedItemLabel} are not sufficient for this action.`;
  }

  return "This action is currently blocked by your entitlement settings.";
};

const buildUsageMeta = (details = {}) => {
  const currentUsage = toNumberOrNull(details.currentUsage);
  const allowedLimit =
    details.allowedLimit === Infinity
      ? Infinity
      : toNumberOrNull(details.allowedLimit);
  const remaining =
    details.remaining === Infinity
      ? Infinity
      : toNumberOrNull(details.remaining);
  const requiredAmount = toNumberOrNull(details.requiredAmount);

  if (
    currentUsage === null &&
    allowedLimit === null &&
    remaining === null &&
    requiredAmount === null
  ) {
    return null;
  }

  return { currentUsage, allowedLimit, remaining, requiredAmount };
};

export const isSupportedBlockerCategory = (category) =>
  SUPPORTED_BLOCKER_CATEGORIES.has(category);

export const buildEntitlementModalModel = ({
  blockerCategory,
  details = {},
  message = null,
  planCode = null,
  context = {},
}) => {
  if (!isSupportedBlockerCategory(blockerCategory)) return null;

  const normalizedDetails = {
    ...details,
    planCode: details?.planCode || planCode || "FREE",
  };

  const blockedItemLabel = resolveBlockerLabel({
    category: blockerCategory,
    details: normalizedDetails,
  });
  const categoryMeta = CATEGORY_META[blockerCategory];
  const title =
    blockedItemLabel && blockedItemLabel !== "This action"
      ? `${blockedItemLabel} Blocked`
      : categoryMeta.title;

  const description = buildCategoryDescription({
    category: blockerCategory,
    blockedItemLabel,
    message,
    details: normalizedDetails,
  });

  const footnote =
    blockerCategory === BLOCKER_CATEGORIES.INSUFFICIENT_CREDITS
      ? "Credits are deducted only after backend confirmation."
      : blockerCategory === BLOCKER_CATEGORIES.QUOTA_EXHAUSTED
        ? "Quotas reset according to your billing period."
        : "Plan entitlements are enforced from backend policy.";

  return {
    blockerCategory,
    blockedItemLabel,
    title,
    description,
    footnote,
    planCode: normalizedDetails.planCode || "FREE",
    details: normalizedDetails,
    usageMeta: buildUsageMeta(normalizedDetails),
    context,
    primaryCta: {
      label: categoryMeta.primaryCtaLabel,
      to: categoryMeta.primaryCtaTo || "/plan",
    },
    secondaryCta: {
      label: categoryMeta.secondaryCtaLabel,
      to: categoryMeta.secondaryCtaTo || "/plan",
    },
  };
};

export const extractEntitlementModalModelFromError = (
  error,
  { fallbackPlanCode = "FREE", context = {} } = {},
) => {
  const payload = error?.response?.data || error || {};
  const blockerCategory = resolveCategoryFromPayload(payload);
  if (!isSupportedBlockerCategory(blockerCategory)) return null;

  const details = {
    ...(payload?.details || {}),
    planCode:
      payload?.details?.planCode || payload?.planCode || fallbackPlanCode,
  };

  return buildEntitlementModalModel({
    blockerCategory,
    details,
    message: payload?.message || null,
    planCode: details.planCode || fallbackPlanCode,
    context,
  });
};
